import type { FC } from 'react';
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'components/atoms/Alert';
import type { AddressResult } from 'hooks/shared/useGoogleAddressAutocomplete';
import FreeTextInput from '../FreeTextInput/FreeTextInput.component';
import SearchIcon from 'assets/svg/icons/Search';
import useGoogleAddressAutocomplete from 'hooks/shared/useGoogleAddressAutocomplete';
import type { AddressSearchFieldAutocompleteProps } from './AddressSearchField.types';
import AddressSearchFieldSuggestions, {
  getAddressSuggestionOptionId,
} from './AddressSearchFieldSuggestions.component';

/**
 * Google Places address lookup: combobox, suggestions, and optional manual entry.
 * Mount only while the structured address form is hidden so autocomplete stays inactive afterward.
 */
const AddressSearchFieldAutocomplete: FC<
  AddressSearchFieldAutocompleteProps
> = ({
  label,
  searchPlaceholder,
  countryRestrictions,
  includedPrimaryTypes,
  onAddressSelect,
  onManualEntry,
}) => {
  const { t } = useTranslation();
  const listboxId = useId();
  const [searchValue, setSearchValue] = useState('');
  const [showStreetNumberWarning, setShowStreetNumberWarning] = useState(false);
  // While a pick is resolving we disable the input and show a spinner so
  // further typing can't race the async Place Details response.
  const [isResolvingPick, setIsResolvingPick] = useState(false);

  const handleManualEntrySelect = useCallback(() => {
    setShowStreetNumberWarning(false);
    onManualEntry?.(searchValue.trim());
  }, [onManualEntry, searchValue]);

  const manualTrailingCount = onManualEntry ? 1 : 0;

  const handleAddressSelect = useCallback(
    (address: AddressResult) => {
      if (!address.hasStreetNumber) {
        setShowStreetNumberWarning(true);
        setSearchValue(address.street);
        return;
      }

      setShowStreetNumberWarning(false);
      setSearchValue('');
      onAddressSelect(address);
    },
    [onAddressSelect],
  );

  const {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange,
    handleSuggestionSelect,
    handleKeyDown,
    closeDropdown,
  } = useGoogleAddressAutocomplete({
    onPlaceSelect: handleAddressSelect,
    countryRestrictions,
    includedPrimaryTypes,
    requirePreciseAddress: true,
    trailingListboxOptionCount: manualTrailingCount,
    onTrailingListboxOptionSelect: onManualEntry
      ? handleManualEntrySelect
      : undefined,
  });

  const trimmedQuery = searchValue.trim();
  const showSuggestionsPanel =
    isOpen &&
    trimmedQuery.length >= 2 &&
    (suggestions.length > 0 || manualTrailingCount > 0);

  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(getAddressSuggestionOptionId(listboxId, activeIndex))
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowStreetNumberWarning(false);
      setSearchValue(e.target.value);
      handleInputChange(e.target.value);
    },
    [handleInputChange],
  );

  const handleBlur = useCallback(() => {
    setTimeout(closeDropdown, 150);
  }, [closeDropdown]);

  const pickSuggestion = useCallback(
    (suggestion: Parameters<typeof handleSuggestionSelect>[0]) => {
      // Clear input synchronously and block further input until the Place
      // Details fetch resolves, so the async response can't clobber text the
      // user typed in the meantime.
      setSearchValue('');
      setIsResolvingPick(true);
      void handleSuggestionSelect(suggestion).finally(() => {
        setIsResolvingPick(false);
      });
    },
    [handleSuggestionSelect],
  );

  return (
    <div>
      {label && (
        <h3 className="mb-1 mt-3 text-primary font-semibold">{label}</h3>
      )}
      <div className="relative">
        <FreeTextInput
          value={searchValue}
          onChange={
            handleChange as React.ComponentProps<
              typeof FreeTextInput
            >['onChange']
          }
          onKeyDown={
            handleKeyDown as React.ComponentProps<
              typeof FreeTextInput
            >['onKeyDown']
          }
          onBlur={handleBlur}
          placeholder={searchPlaceholder}
          Icon={SearchIcon}
          disabled={isResolvingPick}
          loading={isResolvingPick}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestionsPanel}
          aria-controls={showSuggestionsPanel ? listboxId : undefined}
          aria-activedescendant={
            showSuggestionsPanel && activeIndex >= 0
              ? getAddressSuggestionOptionId(listboxId, activeIndex)
              : undefined
          }
        />

        {showSuggestionsPanel && (
          <AddressSearchFieldSuggestions
            listboxId={listboxId}
            suggestions={suggestions}
            activeIndex={activeIndex}
            onPick={pickSuggestion}
            manualEntry={
              onManualEntry
                ? {
                    query: searchValue,
                    onSelect: handleManualEntrySelect,
                  }
                : undefined
            }
          />
        )}

        {showStreetNumberWarning && (
          <Alert
            className="!mb-0 mt-2"
            type="info"
            description={t('address.street_number_or_manual_warning')}
          />
        )}

        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {showSuggestionsPanel
            ? suggestions.length > 0
              ? `${suggestions.length} suggestion${
                  suggestions.length === 1 ? '' : 's'
                } available`
              : t('address.manual_entry_available_sr')
            : ''}
        </div>
      </div>
    </div>
  );
};

AddressSearchFieldAutocomplete.displayName = 'AddressSearchFieldAutocomplete';

export default AddressSearchFieldAutocomplete;
